export enum I9FormStatus {
    SECTION1_PENDING = "SECTION1_PENDING",
    SECTION1_COMPLETED = "SECTION1_COMPLETED",
    SECTION2_COMPLETED = "SECTION2_COMPLETED",
    // A historical I-9 published from the batch-digitization pipeline (VTK-1863). Terminal + read-only
    // in the live workflow (not amendable via Section 1/2 — corrections go through remediation).
    DIGITIZED = "DIGITIZED",
    // A digitized record whose remediation is complete (RQA-03). Terminal, and it inherits every
    // DIGITIZED restriction — still not amendable through the live Section 1/2 path.
    REMEDIATED = "REMEDIATED"
}

/**
 * Statuses for records transcribed from paper rather than filled in the live workflow. They are
 * terminal: read-only to Section 1/2 amendment, and viewed as the original scan rather than a
 * reconstructed form.
 *
 * This exists because REMEDIATED was added to the enum without being added to the several places
 * that special-cased DIGITIZED — which enabled the amend actions on a terminal record. Add a status
 * here once and every restriction picks it up, instead of hunting for `=== DIGITIZED` comparisons.
 */
export const HISTORICAL_I9_FORM_STATUSES: readonly I9FormStatus[] = [
    I9FormStatus.DIGITIZED,
    I9FormStatus.REMEDIATED,
];

export const isHistoricalI9Form = (status: I9FormStatus): boolean =>
    HISTORICAL_I9_FORM_STATUSES.includes(status);

/**
 * Statuses whose Hire Date and Location an employer may still edit directly (VTK-1954).
 *
 * SECTION1_COMPLETED covers BOTH Section 2 Pending cases — employer completion and Remote/NYOC
 * completion — which share the status and differ only by `remoteI9Flag`.
 *
 * Everything else is closed: a completed I-9 is corrected through amendment, and a historical record
 * through remediation. Listed as an allow-list rather than a deny-list so a status added to the enum
 * later is closed until someone decides otherwise — the mistake HISTORICAL_I9_FORM_STATUSES above
 * exists to record. Mirrored server-side, which is the enforcement; this only decides whether to
 * offer the control.
 */
export const EMPLOYMENT_DETAILS_EDITABLE_STATUSES: readonly I9FormStatus[] = [
    I9FormStatus.SECTION1_PENDING,
    I9FormStatus.SECTION1_COMPLETED,
];

export const canEditEmploymentDetails = (status: I9FormStatus): boolean =>
    EMPLOYMENT_DETAILS_EDITABLE_STATUSES.includes(status);

/**
 * Whether a Supplement B may be started from the employee record (VTK-2016). Only once Section 2 is
 * complete: the server refuses a Supplement B on a form whose Section 2 was never signed, and a
 * historical record is reverified through remediation, not the live wizard. An allow-list, like the
 * one above, so a status added later stays closed until someone decides.
 */
export const canStartSupplementB = (status: I9FormStatus): boolean =>
    status === I9FormStatus.SECTION2_COMPLETED;
